/*
 * Pochette cadeau — sélection des articles à offrir, sous le panier.
 *
 * Le client coche « C'est un cadeau », puis choisit CE QU'IL OFFRE. Le
 * composant en déduit l'emballage nécessaire et ajoute la bonne variante au
 * panier. Le client ne choisit jamais un format, il choisit un contenu.
 *
 * ⚠️ UN SEUL emballage est FACTURÉ, 2,90 €, quelle que soit la sélection
 * (décision d'Élisabeth). La quantité de la ligne ne bouge donc jamais, et
 * l'écran n'annonce pas « 2 pochettes » : ce serait faire attendre un prix qui
 * ne bougera pas. Ce qu'il faut physiquement sortir — sacs et rubans — part sur
 * la commande dans la propriété « Emballage ».
 *
 * Pourquoi la sélection et pas une case par ligne : une pochette est un
 * CONTENANT. Trois magnets tiennent dans un seul sac, donc facturer une
 * pochette par article ferait payer trois fois un emballage unique. C'est aussi
 * ce qui répond au cas « je n'offre qu'une partie de ma commande » : la
 * sélection le dit, sans champ libre à interpréter à l'atelier.
 *
 * ⚠️ La ligne pochette est une ligne de panier IMBRIQUÉE (`parent_line_key`,
 * Cart AJAX API) : Shopify la rattache au premier article de la sélection et
 * l'affiche EN RETRAIT sous lui, au panier comme au paiement. C'est le
 * mécanisme que Shopify a prévu pour l'emballage cadeau, et c'est la seule
 * façon d'agir sur sa PLACE — les lignes d'un panier se rangent du plus récent
 * au plus ancien et aucune API ne les réordonne, donc la pochette, ajoutée en
 * dernier, s'affichait sinon EN PREMIER, devant les vrais articles.
 * ⚠️ Conséquence à connaître : retirer l'article parent retire la pochette avec
 * lui, Shopify s'en charge. C'est voulu quand c'était le seul article offert ;
 * quand il y en avait d'autres, la case se retrouve décochée et le client la
 * recoche. Un emballage facturé pour un article parti serait pire.
 *
 * ⚠️ AUCUN état n'est stocké ailleurs que dans le panier. La sélection vit dans
 * les propriétés de la ligne pochette (`Contenu` lisible + `_ids` machine) :
 * elle survit donc au rechargement, au passage tiroir ↔ page panier, et se
 * retrouve telle quelle sur la commande, où le bon de préparation la lit. Un
 * localStorage aurait perdu la sélection d'un appareil à l'autre et n'aurait
 * rien dit au back-office.
 */
(function () {
  'use strict';

  /* --- Barème d'encombrement -------------------------------------------
   *
   * Chaque article vaut un SCORE, chaque sac une CAPACITÉ, et l'article impose
   * en plus une taille MINIMALE (une tasse ne rentre pas dans le petit sac,
   * quel que soit le score). Les cotes des sacs sont celles du fournisseur :
   *   S = 11 × 6 × 14 cm · M = 18 × 10 × 23 cm · L = 26 × 13 × 32 cm
   *
   * ⚠️ Les scores et capacités sont un JUGEMENT, pas une mesure : ils sont
   * calés pour que 4 magnets tiennent dans le S (constaté à la main) et que les
   * rapports suivent grossièrement les volumes des trois sacs (1 : 4,5 : 11,7).
   * Une erreur ici coûte au plus un sac de la taille au-dessus — se tromper
   * dans ce sens est sans gravité, l'inverse laisserait un article dehors. À
   * ajuster ICI, à un seul endroit, après quelques commandes réelles.
   *
   * La CARTE POSTALE vaut 0 : plate, elle se glisse contre la paroi sans
   * prendre de place — même raisonnement que ENVELOPE_FLAT_TYPES côté
   * back-office, où elle décide qu'il faut une enveloppe mais jamais laquelle.
   * Elle impose en revanche le sac M : ses 14,8 cm ne passent pas dans les
   * 11 cm du petit.
   */
  var CLASSES = {
    plat: { score: 0, min: 'M' },
    petit: { score: 1, min: 'S' },
    pile: { score: 2, min: 'S' },
    moyen: { score: 4, min: 'M' },
    grand: { score: 12, min: 'L' },
  };

  var SIZES = ['S', 'M', 'L'];
  var CAPACITY = { S: 4, M: 8, L: 24 };

  /*
   * Classe d'encombrement par type de produit Shopify. Un type ABSENT n'est pas
   * éligible.
   *
   * ⚠️ L'AFFICHE est la seule à ne pas prendre de sac : sans cadre, elle part
   * ROULÉE EN TUBE, et un tube ne rentre dans aucune des trois pochettes — ni
   * même dans les plus grands sacs du fournisseur au-delà de l'A3. Son
   * emballage cadeau est donc un RUBAN et une étiquette noués autour du tube,
   * ce qui marche pour les trois formats.
   * ⚠️ Une affiche ENCADRÉE reste inéligible : 1,3 à 1,7 kg, 60 × 84 cm pour
   * une A1, et elle voyage déjà dans son propre carton (décision d'Élisabeth).
   */
  var RULES = {
    Magnet: { cls: 'petit' },
    'Porte-clés': { cls: 'petit' },
    'Carte postale': { cls: 'plat' },
    Tasse: { cls: 'moyen' },
    Mug: { cls: 'moyen' },
    Carnet: { cls: 'moyen' },
    Torchon: { cls: 'moyen' },
    'Tote bag': { cls: 'moyen' },
    'Tee-shirt': { cls: 'moyen' },
    Gourde: { cls: 'grand' },
    'Dessous de verre (lot de 6)': { cls: 'pile' },
    // Deux types dont l'encombrement dépend de la variante : le premier jeton
    // TROUVÉ dans le libellé de variante gagne, sinon la classe de base.
    'Serviette de bain': { cls: 'moyen', variants: [{ match: '70 cm x 140', cls: 'grand' }] },
    Puzzle: { cls: 'grand', variants: [{ match: '70 pièces', cls: 'moyen' }] },
    // L'affiche : les libellés du catalogue sont « A3 (29,7 x 42 cm) / Sans
    // cadre » ou « … / Cadre noir ».
    //
    // · `unframedOnly` — SANS CADRE, elle part ROULÉE EN TUBE, donc un RUBAN.
    //   TOUS les formats sans exception, l'A4 et les formats « cm »
    //   (30 × 40, 40 × 50) compris (confirmé le
    //   25/08/2026 : son poids de 111 g, posé pour viser la Lettre Verte
    //   suivie, avait fait croire qu'il partait à plat — c'était faux).
    //   Ne pas réintroduire de cas particulier par format sans le demander.
    // · `framedVariants` — ENCADRÉE, elle est INÉLIGIBLE : elle voyage déjà
    //   dans son carton et n'entre pas dans la liste, `classify` l'écarte.
    //   Elle ne retire PAS l'option au RESTE du panier : un panier « affiche
    //   encadrée + magnets » doit pouvoir faire emballer ses magnets. Ce drapeau
    //   ne sert donc qu'à DIRE au client pourquoi l'affiche manque à la liste
    //   (cf. `isFramedPoster`) — sans cette phrase, il croit avoir tout offert.
    //   ⚠️ Deux allers-retours ce 25/08/2026 : l'affiche encadrée a été rendue
    //   éligible à 3,50 € pendant quelques heures (retiré), puis rendue
    //   BLOQUANTE pour tout le panier — ce qui faisait DISPARAÎTRE le bloc
    //   « C'est un cadeau » dès qu'une affiche encadrée y était, signalé par
    //   Élisabeth le 26/08/2026 (« je n'ai plus la proposition d'emballage
    //   cadeau »). Ne pas réintroduire l'un ni l'autre.
    Affiche: { cls: 'tube', unframedOnly: true, framedVariants: true },
    Poster: { cls: 'tube', unframedOnly: true, framedVariants: true },
  };

  /*
   * Formats d'affiche qui RETIRENT l'option au panier ENTIER (21/10/2026,
   * demandé : « qu'il ne soit pas possible de sélectionner la possibilité
   * d'avoir un emballage cadeau si le client a dans son panier des affiches A2
   * et 40 x 50 »). Avec ou sans cadre : c'est le FORMAT qui décide.
   *
   * ⚠️ C'est l'INVERSE de la règle des affiches encadrées, et c'est voulu : la
   * question a été reposée explicitement, en rappelant que le bloc qui
   * disparaissait pour une affiche encadrée avait été annulé le 26/08/2026 —
   * la réponse a été « retirer l'option à tout le panier ». L'encadrée, elle,
   * reste seulement absente de la liste (cf. `isFramedPoster`).
   *
   * Ce sont des PRÉFIXES du libellé de variante, comme les jetons du registre
   * des formats côté back-office (`utils/afficheFormats.js`) : « A2 (42 x
   * 59,4 cm) / Sans cadre », « 40 x 50 cm / Cadre noir ». D'où « 40 x 50 » et
   * jamais « 40 », qui attraperait aussi un ancien « 40 x 60 cm ».
   */
  var BLOCKING_POSTER_FORMATS = ['A2', '40 x 50'];

  /** Cet article retire-t-il l'emballage cadeau au panier entier ? */
  function blocksGift(item) {
    var rule = RULES[item.product_type];
    if (!rule || rule.cls !== 'tube') return false;
    var variant = String(item.variant_title || '');
    return BLOCKING_POSTER_FORMATS.some(function (token) {
      return variant.indexOf(token) === 0;
    });
  }

  /** Classe d'un article du panier, ou null s'il n'est pas éligible. */
  function classify(item) {
    var rule = RULES[item.product_type];
    if (!rule) return null;
    var variant = String(item.variant_title || '');
    // « Sans cadre » se teste en PRÉSENCE, jamais « Cadre » en absence : le
    // second est contenu dans le premier, et chercher « Cadre » classerait une
    // affiche nue parmi les encadrées.
    if (rule.unframedOnly && variant.indexOf('Sans cadre') === -1) return null;
    for (var i = 0; i < (rule.variants || []).length; i++) {
      if (variant.indexOf(rule.variants[i].match) !== -1) return rule.variants[i].cls;
    }
    return rule.cls;
  }

  /**
   * Cet article est-il une affiche ENCADRÉE ?
   *
   * Elle est inéligible — `classify` l'écarte déjà de la liste — et ce test ne
   * sert qu'à EXPLIQUER son absence sous la liste. Il ne retire l'option à
   * personne : un panier « affiche encadrée + magnets » garde le bloc
   * « C'est un cadeau » et fait emballer ses magnets.
   *
   * ⚠️ Le test est POSITIF des deux côtés — « Sans cadre » d'abord, « Cadre »
   * ensuite — et jamais une absence : « Sans cadre » CONTIENT « cadre », et
   * chercher l'absence du mot rangerait une affiche NUE parmi les encadrées.
   * Un libellé qui ne dit ni l'un ni l'autre ne compte pas.
   */
  function isFramedPoster(item) {
    var rule = RULES[item.product_type];
    if (!rule || !rule.framedVariants) return false;
    var variant = String(item.variant_title || '');
    if (variant.indexOf('Sans cadre') !== -1) return false;
    return variant.indexOf('Cadre') !== -1;
  }

  /** Sacs nécessaires pour les articles qui en prennent un. */
  function bagsFor(units) {
    if (!units.length) return null;
    var score = 0;
    var idx = 0;
    units.forEach(function (u) {
      score += CLASSES[u.cls].score * u.quantity;
      idx = Math.max(idx, SIZES.indexOf(CLASSES[u.cls].min));
    });
    // On monte d'un cran tant que ça déborde, puis — seulement si même le grand
    // sac ne suffit pas — on en compte plusieurs.
    while (idx < SIZES.length - 1 && score > CAPACITY[SIZES[idx]]) idx++;
    var size = SIZES[idx];
    return { size: size, count: score > CAPACITY[size] ? Math.ceil(score / CAPACITY[size]) : 1 };
  }

  /**
   * Emballage nécessaire pour une sélection.
   *
   * ⚠️ UN SEUL emballage est FACTURÉ, 2,90 €, quelle que soit la sélection
   * (décision d'Élisabeth). Ce que rend cette fonction n'est donc pas un prix
   * mais ce qu'il faut PHYSIQUEMENT préparer : d'éventuels sacs, et un ruban
   * par affiche. Le détail part sur la commande dans la propriété
   * « Emballage », que reprend le bon de préparation — sans elle, l'atelier
   * devrait deviner combien de sacs sortir.
   *
   * ⚠️ Il n'y a plus de VARIANTE à choisir : le produit Shopify n'en a qu'une
   * (cf. l'en-tête de `snippets/gift-pouch.liquid` — la caisse affichait sinon
   * « Emballage cadeau / Ruban » sous le nom de l'article). Le poids déclaré à
   * Shopify est donc le même pour tout emballage ; le détail physique voyage
   * dans la propriété `_Emballage`.
   *
   * @param {Array<{cls: string, quantity: number}>} units
   * @returns {{bags: {size: string, count: number}|null,
   *            ribbons: number}|null} null si la sélection est vide.
   */
  function packagingFor(units) {
    var bagUnits = units.filter(function (u) {
      return u.cls !== 'tube';
    });
    var ribbons = units.reduce(function (n, u) {
      return u.cls === 'tube' ? n + u.quantity : n;
    }, 0);
    var bags = bagsFor(bagUnits);
    if (!bags && !ribbons) return null;
    return { bags: bags, ribbons: ribbons };
  }

  /** « 1 pochette M + 2 rubans » — ce que l'atelier doit sortir. */
  function packagingLabel(pkg) {
    var parts = [];
    if (pkg.bags) {
      parts.push(
        pkg.bags.count + ' pochette' + (pkg.bags.count > 1 ? 's' : '') + ' ' + pkg.bags.size
      );
    }
    if (pkg.ribbons) parts.push(pkg.ribbons + ' ruban' + (pkg.ribbons > 1 ? 's' : ''));
    return parts.join(' + ');
  }

  /**
   * Libellé d'un article, VARIANTE COMPRISE. Sans elle, trois affiches d'une
   * même ville se lisent « Affiche - Ancenis-Saint-Géron » trois fois : ni le
   * client ne sait laquelle il coche, ni l'atelier laquelle emballer.
   * Séparateur « · » et non « - », déjà pris par « Type - Ville ».
   */
  function itemLabel(item) {
    var label = item.product_title;
    if (item.variant_title) label += ' · ' + item.variant_title;
    return label + (item.quantity > 1 ? ' ×' + item.quantity : '');
  }

  /** Clé de l'article auquel une ligne est rattachée, ou null si elle est libre. */
  function currentParentKey(item) {
    return (item && item.parent_relationship && item.parent_relationship.parent_key) || null;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /*
   * Racine de la boutique. `window.routes` est posé par le thème et porte déjà
   * le préfixe de langue ; `Shopify.routes.root` n'est qu'un filet. Coder « /
   * cart.js » en dur casserait sur une boutique multilingue.
   */
  function cartJsonUrl() {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'cart.js';
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    });
  }

  class GiftPouch extends HTMLElement {
    connectedCallback() {
      var raw = this.querySelector('[data-gift-pouch-config]');
      try {
        this.config = JSON.parse(raw.textContent);
      } catch (e) {
        return; // Pas de configuration exploitable : le bloc reste muet.
      }
      this.body = this.querySelector('[data-gift-pouch-body]');
      if (!this.started) {
        this.started = true;
        this.busy = false;
        this.reads = 0;
        this.selection = null; // Set d'identifiants de variante, null = pas encore lu.
        this.addEventListener('change', this.onChange.bind(this));
      }
      // Le panier peut bouger ailleurs (quantité modifiée, article retiré) :
      // on se relit plutôt que de raisonner sur un état devenu faux.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'gift-pouch') return;
          return this.refresh();
        });
      }
      if (!this.pending) this.refresh();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    /** Relit le panier et redessine. */
    refresh() {
      var read = ++this.reads;
      return fetch(cartJsonUrl())
        .then(function (r) {
          return r.json();
        })
        .then((cart) => {
          if (read !== this.reads) return;
          this.cart = cart;
          // Une affiche A2 ou 40 × 50 retire l'option au panier ENTIER (cf.
          // `blocksGift`) : liste vide, donc bloc masqué, et la sélection
          // filtrée ci-dessous se vide — `sync()` retire alors un emballage
          // déjà ajouté avant elle.
          var blocked = cart.items.some(blocksGift);
          this.eligible = blocked
            ? []
            : cart.items.filter(
                (item) => item.product_id !== this.config.productId && classify(item)
              );
          var pouchLine = cart.items.find((item) => item.product_id === this.config.productId);
          // La sélection se relit dans le panier lui-même (propriété `_ids` de
          // la ligne pochette). Une sélection gardée dans le composant se
          // serait perdue au moindre rechargement.
          if (!this.pending) {
            this.selection = pouchLine ? new Set(this.readIds(pouchLine)) : null;
          }
          // Un article sorti du panier sort de la sélection : sinon la pochette
          // resterait facturée pour un contenu qui n'existe plus.
          if (this.selection) {
            var present = new Set(this.eligible.map((i) => i.key));
            this.selection = new Set([...this.selection].filter((k) => present.has(k)));
          }
          // Une affiche ENCADRÉE est simplement absente de la liste : elle
          // n'est pas emballable, mais elle ne retire l'option à AUCUN des
          // autres articles. On retient seulement qu'il y en a une, pour le
          // dire sous la liste.
          this.framedPresent = this.cart.items.some(isFramedPoster);
          this.render();
          // Le panier a pu changer sans nous : on recale la pochette si besoin.
          if (!this.busy && pouchLine) this.sync();
        })
        .catch(function () {
          /* Panier illisible : on laisse l'écran tel quel plutôt que de le vider. */
        });
    }

    announce() {
      if (!this.changed) return;
      this.changed = false;
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-pouch', cartData: this.cart });
      }
    }

    readIds(pouchLine) {
      var raw = (pouchLine.properties && pouchLine.properties._ids) || '';
      return String(raw).split(',').filter(Boolean);
    }

    render() {
      if (!this.eligible.length) {
        this.hidden = true;
        this.drawn = '';
        this.body.innerHTML = '';
        return;
      }
      this.hidden = false;
      var on = this.selection !== null;
      var html =
        '<label class="gift-pouch__toggle">' +
        '<input type="checkbox" data-gift-pouch-toggle' +
        (on ? ' checked' : '') +
        '>' +
        '<span class="gift-pouch__title">🎁 C\'est un cadeau</span>' +
        // Le « + » dit que ce montant S'AJOUTE au panier, au lieu de se lire
        // comme le prix d'un article de plus dans la liste.
        '<span class="gift-pouch__price">+' +
        escapeHtml(this.config.priceLabel) +
        '</span>' +
        '</label>';

      if (on) {
        html +=
          '<div class="gift-pouch__panel">' +
          '<p class="gift-pouch__hint">Nous vous fournissons un emballage cadeau. ' +
          'Pour ajuster la taille de l\'emballage, cochez les articles concernés.</p>' +
          '<ul class="gift-pouch__list">';
        this.eligible.forEach((item) => {
          var checked = this.selection.has(item.key) ? ' checked' : '';
          html +=
            '<li><label><input type="checkbox" data-gift-pouch-item="' +
            escapeHtml(item.key) +
            '"' +
            checked +
            '><span>' +
            escapeHtml(item.product_title) +
            (item.quantity > 1 ? ' <em>×' + item.quantity + '</em>' : '') +
            (item.variant_title
              ? '<small class="gift-pouch__variant">' + escapeHtml(item.variant_title) + '</small>'
              : '') +
            '</span></label></li>';
        });
        html += '</ul>';
        // Une affiche ENCADRÉE manque à la liste : on dit pourquoi, sinon le
        // client croit avoir tout fait emballer. C'est la SEULE note admise
        // sous la liste (cf. juste en dessous).
        if (this.framedPresent) {
          html +=
            '<p class="gift-pouch__note">Les affiches encadrées voyagent déjà ' +
            'protégées dans leur carton : elles ne peuvent pas être emballées.</p>';
        }
        // Aucune AUTRE note sous la liste, et c'est voulu (25/08/2026).
        // · Sur le NOMBRE d'emballages : il n'en est facturé qu'un, quelle que
        //   soit la sélection. Annoncer « 2 pochettes » ferait attendre au
        //   client un prix qui ne bougera pas.
        // · Sur une sélection VIDE : elle ne bloque rien — la ligne pochette
        //   est simplement retirée du panier par `sync()`. Le message qui
        //   demandait de cocher un article ou de décocher l'option se lisait
        //   comme une erreur alors qu'il n'y avait rien à corriger.
        html += '</div>';
      }
      if (html === this.drawn) return;
      this.drawn = html;
      this.body.innerHTML = html;
    }

    /** Articles cochés, ramenés à leur classe d'encombrement. */
    selectedUnits() {
      if (!this.selection) return [];
      return this.eligible
        .filter((item) => this.selection.has(item.key))
        .map((item) => ({ cls: classify(item), quantity: item.quantity }));
    }

    onChange(event) {
      var target = event.target;
      if (target.hasAttribute('data-gift-pouch-toggle')) {
        this.selection = target.checked ? new Set(this.eligible.map((i) => i.key)) : null;
      } else if (target.hasAttribute('data-gift-pouch-item')) {
        var key = target.getAttribute('data-gift-pouch-item');
        if (target.checked) this.selection.add(key);
        else this.selection.delete(key);
      } else {
        return;
      }
      event.stopPropagation();
      this.pending = true;
      this.render();
      this.sync();
    }

    /**
     * Aligne le panier sur la sélection : au plus une suppression et un ajout.
     * Ne fait RIEN si la ligne en place est déjà la bonne — sans ce contrôle,
     * la republication de `cartUpdate` relancerait une mise à jour en boucle.
     */
    sync() {
      if (this.busy) {
        this.queued = true;
        return;
      }
      var pkg = packagingFor(this.selectedUnits());
      var current = this.cart.items.find((item) => item.product_id === this.config.productId);
      var wantedId = pkg ? this.config.variantId : null;
      if (!wantedId && !current) {
        this.pending = false;
        this.announce();
        return;
      }

      var ids = this.selection ? this.eligible.filter((i) => this.selection.has(i.key)) : [];
      var idsValue = ids.map((i) => i.key).join(',');
      // Article auquel la pochette est RATTACHÉE (cf. l'en-tête du fichier) :
      // le premier de la sélection, dans l'ordre du panier.
      var parentKey = ids.length ? ids[0].key : null;
      // UN SEUL emballage facturé : la quantité de la ligne ne bouge jamais.
      if (
        current &&
        wantedId &&
        current.variant_id === wantedId &&
        current.quantity === 1 &&
        ((current.properties && current.properties._ids) || '') === idsValue &&
        currentParentKey(current) === parentKey
      ) {
        this.pending = false;
        this.announce();
        return;
      }

      this.busy = true;
      var chain = Promise.resolve();
      if (current) {
        chain = chain.then(() =>
          postJson(routes.cart_change_url, { id: current.key, quantity: 0 })
        );
      }
      if (wantedId && parentKey) {
        // Séparées par « ; » et non « , » : un libellé de variante en contient
        // déjà (« A3 (29,7 x 42 cm) / Sans cadre »), et la liste deviendrait
        // illisible sur le bon de préparation.
        var contents = ids.map(itemLabel).join(' ; ');
        chain = chain.then(() =>
          postJson(routes.cart_add_url, {
            items: [
              {
                id: wantedId,
                quantity: 1,
                // Ligne de panier IMBRIQUÉE : la pochette s'affiche en retrait
                // sous l'article auquel elle est rattachée, au panier comme au
                // paiement, au lieu de s'intercaler comme un article de plus.
                parent_line_key: parentKey,
                // `Contenu` est LISIBLE : elle s'affiche au client, dans la
                // commande Shopify et sur le bon de préparation — c'est elle
                // qui dit quoi glisser dans le sac. `_ids` est technique
                // (préfixe `_` = masquée côté client) et sert à retrouver la
                // sélection quand le client revient sur son panier.
                // ⚠️ Les TROIS propriétés sont PRIVÉES (préfixe `_`) : Shopify
                // les masque au panier, à la caisse et dans l'e-mail de
                // confirmation, tout en les enregistrant sur la commande, où le
                // back-office les lit. Le client a déjà sa sélection sous les
                // yeux dans le bloc « C'est un cadeau » — la répéter sous la
                // ligne de paiement n'apprenait rien et l'alourdissait.
                properties: {
                  // Ce que le client offre, variante comprise.
                  _Contenu: contents,
                  // Ce qu'il faut PHYSIQUEMENT sortir. Un seul emballage est
                  // facturé, mais la sélection peut en demander plusieurs :
                  // sans cette ligne, l'atelier devrait le recalculer de tête.
                  _Emballage: packagingLabel(pkg),
                  _ids: idsValue,
                },
              },
            ],
          })
        );
      }
      return chain
        .then(() => fetch(cartJsonUrl()).then((r) => r.json()))
        .then((cart) => {
          this.cart = cart;
          this.busy = false;
          this.changed = true;
          if (this.queued) {
            this.queued = false;
            return this.sync();
          }
          this.pending = false;
          this.announce();
        })
        .catch(() => {
          this.busy = false;
          this.queued = false;
          this.pending = false;
          // Le panier n'a pas suivi : on se recale sur ce qu'il dit vraiment
          // plutôt que d'afficher une sélection qui n'a pas été enregistrée.
          this.refresh();
        });
    }
  }

  if (!customElements.get('gift-pouch')) customElements.define('gift-pouch', GiftPouch);

  // Exposé pour pouvoir ÉPROUVER le barème hors navigateur
  // (`node tools/test-gift-pouch.js`). C'est la seule partie de ce fichier qui
  // décide quelque chose ; le reste n'est que du DOM et des appels panier.
  window.GiftPouchRule = {
    classify: classify,
    isFramedPoster: isFramedPoster,
    blocksGift: blocksGift,
    itemLabel: itemLabel,
    packagingFor: packagingFor,
    packagingLabel: packagingLabel,
    CAPACITY: CAPACITY,
  };
})();
